// frontend/src/pages/admin/GlobalNotificationsManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Input, Popconfirm, Badge, Tooltip, Modal } from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import api from '../../utils/api';
import './users.css';
import NotificationForm from './NotificationForm';
import dayjs from 'dayjs';
import { useAppContext } from '../../context/AppContext';
import { useNotificationApi } from '../../context/NotificationContext';

const { Search } = Input;

const GlobalNotificationsManagement = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [sortColumn, setSortColumn] = useState('id');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [deletingId, setDeletingId] = useState(null);

  const { appSettings } = useAppContext();
  const dateFormat = appSettings?.dateFormat || 'DD/MM/YYYY';
  const timeFormat = appSettings?.timeFormat || 'hh:mm A';

  const notificationApi = useNotificationApi();

  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  const fetchNotifications = useCallback(async (triggeredByAction = false) => {
    setLoading(true);
    const loadingKey = 'fetchNotificationsLoading';
    const hideLoading = showLoading(loadingKey, 'Loading notifications...');

    try {
      const params = {
        sort: sortColumn,
        order: sortOrder,
      };

      if (searchText) {
        params.search = searchText;
      }

      const response = await api.get('/api/notifications/global', { 
        params,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      let responseData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.data || []);

      if (!Array.isArray(responseData)) {
        console.warn('Invalid notifications data format');
        responseData = [];
      }

      const mappedNotifications = responseData.map(n => ({
        id: n.id,
        title: n.title,
        message: n.message,
        createdBy: n.createdBy || n.created_by,
        createdAt: n.createdAt || n.created_at,
        updatedAt: n.updatedAt || n.updated_at,
        expiresAt: n.expiresAt || n.expires_at,
        isActive: n.isActive ?? n.is_active ?? true
      }));

      setNotifications(mappedNotifications);
      hideLoading();

      if (!triggeredByAction) {
        showSuccess(
          'Notifications Loaded', 
          mappedNotifications.length > 0 
            ? `${mappedNotifications.length} notifications loaded`
            : 'No notifications found',
          'fetchNotificationsSuccess'
        );
      }
    } catch (error) {
      hideLoading();
      console.error('Error fetching notifications:', error);
      showError('Failed to Fetch', error.response?.data?.message || 'Error loading notifications');
    } finally {
      setLoading(false);
    }
  }, [searchText, sortColumn, sortOrder, showLoading, showSuccess, showError]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const filteredNotifications = (notifications || []).filter(notification => {
    return (notification.title || '').toLowerCase().includes(searchText.toLowerCase()) || 
           (notification.message || '').toLowerCase().includes(searchText.toLowerCase());
  });

  const handleSearch = () => {
    fetchNotifications(true);
  };

  const handleAddNotification = () => {
    setEditingNotification(null);
    setIsModalVisible(true);
  };

  const handleEditNotification = (notification) => {
    setEditingNotification(notification);
    setIsModalVisible(true);
  };

  const handleDelete = async (id, title) => {
    setDeletingId(id);
    const loadingKey = `deleteNotification-${id}`;
    const hideLoading = showLoading(loadingKey, `Deleting "${title}"...`);

    try {
      await api.delete(`/api/notifications/global/${id}`);
      hideLoading();
      showSuccess('Notification Deleted', `"${title}" was deleted successfully!`, 'deleteNotificationSuccess');
      fetchNotifications(true);
    } catch (error) {
      hideLoading();
      console.error('Error deleting notification:', error.response?.data || error.message, error);
      
      let errorMessage = error.response?.data?.message || `Failed to delete notification "${title}".`;
      showError('Deletion Failed', errorMessage, `deleteNotificationError-${id}`);
    } finally {
      setDeletingId(null);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (currentSorter && currentSorter.field && currentSorter.order) {
      const order = currentSorter.order === 'ascend' ? 'ASC' : 'DESC';
      setSortColumn(currentSorter.field);
      setSortOrder(order);
      fetchNotifications(true);
    } else {
      setSortColumn('id');
      setSortOrder('DESC');
      fetchNotifications(true);
    }
  };

  const columns = [
    {
      title: 'Status',
      dataIndex: 'expiresAt',
      key: 'status',
      width: '10%',
      render: (expiresAt, record) => {
        const isExpired = expiresAt && new Date(expiresAt) < new Date();
        const isActive = record.isActive;
        
        if (!isActive) {
          return <Badge status="default" text="Disabled" />;
        }
        return (
          <Badge
            status={isExpired ? 'error' : 'success'}
            text={isExpired ? 'Expired' : 'Active'}
          />
        );
      },
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Expired', value: 'expired' },
        { text: 'Disabled', value: 'disabled' },
      ],
      onFilter: (value, record) => {
        const isExpired = record.expiresAt && new Date(record.expiresAt) < new Date();
        if (value === 'active') return !isExpired && record.isActive;
        if (value === 'expired') return isExpired && record.isActive;
        if (value === 'disabled') return !record.isActive;
        return true;
      },
      sorter: (a, b) => {
        const aExpired = a.expiresAt && new Date(a.expiresAt) < new Date();
        const bExpired = b.expiresAt && new Date(b.expiresAt) < new Date();
        
        if (!a.isActive && !b.isActive) return 0;
        if (!a.isActive) return 1;
        if (!b.isActive) return -1;
        
        return (aExpired ? 1 : 0) - (bExpired ? 1 : 0);
      },
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: '20%',
      sorter: true,
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      width: '30%',
      render: (text) => (
        <div className="notification-message">
          {text}
        </div>
      ),
    },
    {
      title: 'Expires At',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (date) => date ? dayjs(date).format(`${dateFormat} ${timeFormat}`) : 'Never',
      width: '15%',
      sorter: (a, b) => new Date(a.expiresAt || 0) - new Date(b.expiresAt || 0),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format(`${dateFormat} ${timeFormat}`),
      width: '15%',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '10%',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditNotification(record)}
              aria-label={`Edit ${record.title}`}
            />
          </Tooltip>
          <Popconfirm
            title={`Delete "${record.title}"?`}
            description="This cannot be undone"
            onConfirm={() => handleDelete(record.id, record.title)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true, loading: deletingId === record.id }}
          >
            <Tooltip title="Delete">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                aria-label={`Delete ${record.title}`}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="notifications-page">
      <div className="notifications-header">
        <h2>Global Notifications Management</h2>
        <div className="notifications-actions">
          <Space size="middle">
            <Search
              placeholder="Search notifications..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
              style={{ width: 300 }}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddNotification}
              size="large"
            >
              Add Notification
            </Button>
          </Space>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={filteredNotifications}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
        onChange={handleTableChange}
        locale={{ emptyText: 'No notifications found' }}
      />

      <NotificationForm
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSuccess={() => {
          setIsModalVisible(false);
          fetchNotifications(true);
        }}
        notification={editingNotification}
      />
    </div>
  );
};

export default GlobalNotificationsManagement;